/**
 * #89 · La ficha de una etiqueta. Se abre con el CÓDIGO IMPRESO.
 *
 * La ruta es `/labels/units/:code` y nunca `/labels/units/:unitId`. El id
 * interno va ofuscado con Feistel justamente para que el orden y el volumen de
 * serialización de la marca no se filtren; ponerlo en una URL lo devuelve al
 * mundo, y además queda en el historial del navegador y en el log del proxy.
 * Ninguna respuesta de este módulo lo trae.
 *
 * Bloques, en el orden en que se los mira durante una investigación:
 *
 *  1. **Veredicto y QR** — qué contesta el sistema hoy y a dónde apunta la
 *     etiqueta. El QR se puede bajar para probar con un teléfono.
 *  2. **Banderas** — la tabla de la maqueta, cada casillero con la frase que
 *     dice de dónde sale. Un casillero sin explicación es un casillero con el
 *     que alguien decide un caso a ciegas.
 *  3. **Identidad** — lote, producto, territorio, rollo, fechas.
 *  4. **Acciones** — sólo las que la máquina de estados todavía permite. La
 *     lista la manda el servidor (`allowed_actions`), no se calcula acá: dos
 *     copias de una máquina de estados divergen.
 *  5. **Historial** — append-only, con el motivo y quién lo escribió.
 */
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useApiQuery } from '@/api/hooks';
import { labelUnitKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { cn } from '@/ui/cn';
import { ErrorState, QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { DetailList, DetailRow } from '../products/ui/fields';
import { downloadEvidence, downloadLabelQr, fetchLabelQr, svgDataUrl } from './assets';
import { StateBadge, VerdictBadge } from './badges';
import { LabelActionDialog } from './LabelActionDialog';
import { LabelsTabs } from './LabelsTabs';
import {
  ACTION_LABEL_KEYS,
  FLAG_SPECS,
  LABEL_ACTIONS,
  stateLabel,
  type LabelActionName,
} from './units';

type LabelDetail = Schemas['LabelDetailOut'];

/** Rol mínimo de cada acción. Espeja lo que exige la API; acá sólo evita
 *  mostrar un botón que sólo puede terminar en 403. */
const ACTION_MIN_ROLE: Record<LabelActionName, 'analyst' | 'admin'> = {
  stolen: 'admin',
  compromised: 'admin',
  void: 'analyst',
};

export function LabelDetailPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { can } = useAuth();
  const api = useApi();
  const params = useParams<{ code: string }>();
  const code = (params.code ?? '').toUpperCase();
  const valid = code.length > 0;

  const [action, setAction] = useState<LabelActionName | null>(null);
  const [busy, setBusy] = useState<'qr' | 'evidence' | null>(null);
  const [assetError, setAssetError] = useState<unknown>(null);

  const label = useApiQuery<LabelDetail>(
    labelUnitKeys.detail(code),
    `/api/v1/labels/${encodeURIComponent(code)}`,
    undefined,
    { enabled: valid },
  );

  /*
   * El QR no puede ir por `useApiQuery`: la respuesta es `image/svg+xml` y el
   * cliente tipado parsea JSON. Va por `useQuery` con un `queryFn` propio
   * (ver `assets.ts`), así que conserva cache, deduplicación y reintentos —
   * que es lo que la regla del andamio protege.
   */
  const qr = useQuery<string>({
    queryKey: labelUnitKeys.qr(code),
    queryFn: () => fetchLabelQr(code, { api }),
    enabled: valid,
    // El símbolo es función del código y del entorno: no cambia nunca.
    staleTime: Infinity,
  });

  async function runAsset(kind: 'qr' | 'evidence'): Promise<void> {
    setBusy(kind);
    setAssetError(null);
    try {
      if (kind === 'qr') await downloadLabelQr(code, { api });
      else await downloadEvidence(code, { api });
    } catch (error) {
      setAssetError(error);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <LabelsTabs active="units" />

      <QueryBoundary query={label}>
        {(data) => (
          <div className="space-y-4">
            {/* ── 1. la ficha: veredicto, QR y los datos de arriba ──── */}
            <Card>
              <CardHeader
                title={
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span className="text-ink-subtle">{t('labels.units.detail.label')}</span>
                    <code className="font-mono text-base text-ink">{data.code}</code>
                  </span>
                }
                description={
                  <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    {[
                      data.product?.name,
                      data.batch === null || data.batch === undefined
                        ? null
                        : t('labels.units.detail.batchInline', { lot: data.batch.lot_code }),
                      data.roll === null || data.roll === undefined
                        ? null
                        : t('labels.units.detail.rollInline', {
                            roll: data.roll.roll_code ?? '—',
                          }),
                    ]
                      .filter((part): part is string => typeof part === 'string' && part !== '')
                      .map((part, index, all) => (
                        <span key={part}>
                          {part}
                          {index < all.length - 1 && <span className="px-1 text-ink-subtle">·</span>}
                        </span>
                      ))}
                  </span>
                }
              />
              <CardBody className="space-y-5">
                <div className="flex flex-wrap items-center gap-2">
                  <VerdictBadge verdict={data.verdict} />
                  <StateBadge state={data.status_name} />
                  {data.open_alerts > 0 && (
                    <Badge tone="critical">
                      {t('labels.units.detail.openAlertsBadge', {
                        count: fmt.number(data.open_alerts),
                      })}
                    </Badge>
                  )}
                </div>

                {/* El QR va centrado y grande: en esta pantalla es el objeto,
                    no un adorno al margen. Debajo, la URL que codifica en
                    monoespaciada — es lo que se compara contra el teléfono
                    cuando algo no cuadra. */}
                <div className="flex flex-col items-center gap-3">
                  {qr.isPending && (
                    <div className="size-52 animate-pulse rounded bg-border/60" aria-hidden="true" />
                  )}
                  {qr.isError && <ErrorState error={qr.error} />}
                  {qr.data !== undefined && (
                    <img
                      src={svgDataUrl(qr.data)}
                      alt={t('labels.units.qr.alt', { code: data.code })}
                      className="size-52 rounded-(--radius-card) border border-border bg-white p-3"
                    />
                  )}
                  <code className="max-w-full text-center font-mono text-xs break-all text-ink-muted">
                    {data.consumer_url}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => {
                      void runAsset('qr');
                    }}
                    disabled={busy !== null}
                  >
                    {busy === 'qr' ? t('labels.units.qr.downloading') : t('labels.units.qr.download')}
                  </Button>
                </div>

                <p className="text-center text-xs text-ink-subtle">{t('labels.units.qr.hint')}</p>
              </CardBody>
            </Card>

            {/* ── 2. banderas y acciones, una al lado de la otra ────── */}
            <Card>
              <CardBody className="grid gap-6 md:grid-cols-2">
                <section>
                  <h3 className="mb-3 text-xs font-semibold tracking-[0.08em] text-ink-subtle uppercase">
                    {t('labels.units.flags.title')}
                  </h3>
                  <ul className="divide-y divide-border rounded-(--radius-card) border border-border">
                    {FLAG_SPECS.map((spec) => {
                      const on = data.flags[spec.key];
                      return (
                        <li
                          key={spec.key}
                          className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                        >
                          {/* La frase de origen va VISIBLE y no en un
                              `title`: dice qué subsistema prende la bandera,
                              que es lo primero que pregunta quien no entiende
                              por qué está encendida. En un tooltip no existe
                              para quien usa el panel con el dedo. */}
                          <span className="min-w-0">
                            <span className="block text-ink">{t(spec.labelKey)}</span>
                            <span className="block text-xs text-ink-subtle">
                              {t(spec.sourceKey)}
                            </span>
                          </span>
                          {/* El color solo no alcanza —"Sí" en rojo y "Sí" en
                              verde son la misma palabra para quien no
                              distingue el tono—, así que la bandera crítica
                              encendida además va en negrita. */}
                          <span
                            className={cn(
                              'shrink-0 font-medium',
                              on && spec.critical
                                ? 'font-semibold text-critical'
                                : on
                                  ? 'text-verified'
                                  : 'text-ink-muted',
                            )}
                          >
                            {on ? t('labels.units.flags.yes') : t('labels.units.flags.no')}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </section>

                <section>
                  <h3 className="mb-3 text-xs font-semibold tracking-[0.08em] text-ink-subtle uppercase">
                    {t('labels.units.actions.title')}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {data.allowed_actions.length === 0 ? (
                      <p className="text-sm text-ink-muted">{t('labels.units.actions.terminal')}</p>
                    ) : (
                      LABEL_ACTIONS.filter((name) =>
                        (data.allowed_actions as readonly string[]).includes(name),
                      ).map((name) =>
                        can(ACTION_MIN_ROLE[name]) ? (
                          <Button
                            key={name}
                            // Dar de baja es reversible; marcar falsificada o
                            // robada, no. Que no compartan color es la
                            // diferencia que hay que ver antes de apretar.
                            variant={name === 'void' ? 'warning' : 'critical'}
                            className="justify-start"
                            onClick={() => {
                              setAction(name);
                            }}
                          >
                            {t(ACTION_LABEL_KEYS[name])}
                          </Button>
                        ) : null,
                      )
                    )}
                    <Button
                      variant="secondary"
                      className="justify-start"
                      onClick={() => {
                        void runAsset('evidence');
                      }}
                      disabled={busy !== null}
                    >
                      {busy === 'evidence'
                        ? t('labels.units.evidence.downloading')
                        : t('labels.units.evidence.download')}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-ink-subtle">{t('labels.units.evidence.privacy')}</p>
                  {assetError !== null && (
                    <div className="mt-3">
                      <ErrorState error={assetError} />
                    </div>
                  )}
                </section>
              </CardBody>

              {/* El pie: lo que se mira de reojo para ubicar la etiqueta en el
                  tiempo y en el mapa, sin abrir el historial. */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border px-4 py-3 text-xs text-ink-subtle">
                <span>
                  {t('labels.units.detail.scans')}: {fmt.number(data.scan_count)}
                </span>
                <span>
                  {t('labels.units.detail.territory')}: {data.territory ?? '—'}
                </span>
                <span>
                  {t('labels.units.detail.created')}: {fmt.dateTime(data.created_at)}
                </span>
                <span>
                  {t('labels.units.detail.firstScan')}:{' '}
                  {data.first_scan_at === null || data.first_scan_at === undefined
                    ? '—'
                    : fmt.dateTime(data.first_scan_at)}
                </span>
                <span>
                  {t('labels.units.detail.riskScore')}: {fmt.number(data.risk_score)}
                </span>
              </div>
            </Card>

            {/* ── 3. identidad ────────────────────────────────────── */}
            <Card>
              <CardHeader title={t('labels.units.identity.title')} />
              <CardBody>
                <DetailList>
                  <DetailRow
                    label={t('labels.units.detail.batch')}
                    value={
                      data.batch === null || data.batch === undefined ? (
                        '—'
                      ) : (
                        <Link
                          to={`/batches/${String(data.batch.id)}`}
                          className="font-mono text-accent underline underline-offset-2"
                        >
                          {data.batch.lot_code}
                        </Link>
                      )
                    }
                  />
                  <DetailRow
                    label={t('labels.units.detail.expiry')}
                    value={
                      data.batch?.expiry_date === null || data.batch?.expiry_date === undefined
                        ? '—'
                        : fmt.expiry(data.batch.expiry_date)
                    }
                  />
                  <DetailRow
                    label={t('labels.units.detail.product')}
                    value={
                      data.product === null || data.product === undefined
                        ? '—'
                        : `${data.product.sku} · ${data.product.name}`
                    }
                  />
                  <DetailRow
                    label={t('labels.units.detail.territory')}
                    value={data.territory ?? '—'}
                  />
                  <DetailRow
                    label={t('labels.units.detail.roll')}
                    value={
                      data.roll === null || data.roll === undefined ? (
                        '—'
                      ) : (
                        <Link
                          to={`/labels/rolls/${String(data.roll.id)}`}
                          className="font-mono text-accent underline underline-offset-2"
                        >
                          {data.roll.roll_code ?? `#${String(data.roll.id)}`}
                        </Link>
                      )
                    }
                  />
                  <DetailRow
                    label={t('labels.units.detail.created')}
                    value={fmt.dateTime(data.created_at)}
                  />
                  <DetailRow
                    label={t('labels.units.detail.activated')}
                    value={
                      data.activated_at === null || data.activated_at === undefined
                        ? '—'
                        : fmt.dateTime(data.activated_at)
                    }
                  />
                  <DetailRow
                    label={t('labels.units.detail.firstScan')}
                    value={
                      data.first_scan_at === null || data.first_scan_at === undefined
                        ? '—'
                        : `${fmt.dateTime(data.first_scan_at)}${
                            data.first_country !== null && data.first_country !== undefined
                              ? ` · ${data.first_country}`
                              : ''
                          }`
                    }
                  />
                  <DetailRow
                    label={t('labels.units.detail.lastScan')}
                    value={
                      data.last_scan_at === null || data.last_scan_at === undefined
                        ? '—'
                        : fmt.dateTime(data.last_scan_at)
                    }
                  />
                </DetailList>
              </CardBody>
            </Card>

            {/* ── 4. historial ────────────────────────────────────── */}
            <Card>
              <CardHeader
                title={t('labels.units.history.title')}
                description={t('labels.units.history.description')}
              />
              {data.history.length === 0 ? (
                <CardBody>
                  <p className="text-sm text-ink-muted">{t('labels.units.history.empty')}</p>
                </CardBody>
              ) : (
                <Table caption={t('labels.units.history.description')}>
                  <thead>
                    <tr>
                      <Th>{t('labels.units.history.column.when')}</Th>
                      <Th>{t('labels.units.history.column.change')}</Th>
                      <Th>{t('labels.units.history.column.reason')}</Th>
                      <Th>{t('labels.units.history.column.actor')}</Th>
                      <Th>{t('labels.units.history.column.audit')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map((entry) => (
                      <tr key={entry.id}>
                        <Td className="text-ink-muted">{fmt.dateTime(entry.occurred_at)}</Td>
                        <Td>
                          {stateLabel(entry.from_status_name, t)}
                          {' → '}
                          {stateLabel(entry.to_status_name, t)}
                        </Td>
                        <Td>
                          {entry.reason}
                          {entry.reference !== null && entry.reference !== undefined && (
                            <span className="ml-1 font-mono text-xs text-ink-subtle">
                              ({entry.reference})
                            </span>
                          )}
                        </Td>
                        <Td className="text-ink-muted">{entry.actor_email ?? '—'}</Td>
                        <Td className="font-mono text-xs text-ink-subtle">
                          {entry.audit_seq === null || entry.audit_seq === undefined
                            ? '—'
                            : `#${String(entry.audit_seq)}`}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>

            {action !== null && (
              <LabelActionDialog
                code={data.code}
                action={action}
                fromStateLabel={stateLabel(data.status_name, t)}
                onClose={() => {
                  setAction(null);
                }}
              />
            )}
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}
